'use client';

import type { ButtonHTMLAttributes, FC, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { useUiMotionEnabled } from '../../hooks/useMotion';
import { Spinner } from './Spinner';
import './Button.css';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'destructiveSecondary'
    | 'success'
    | 'successSecondary'
    | 'white'
    | 'share'
    | 'hero'
    | 'cta'
    | 'telegram'
    | 'steam';
  mode?: 'primary' | 'secondary' | 'tertiary' | 'success';
  size?: 's' | 'm' | 'l';
  stretched?: boolean;
  loading?: boolean;
  before?: ReactNode;
  after?: ReactNode;
  iconPosition?: 'left' | 'top';
  as?: 'button' | 'span';
}

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 border-none rounded-full font-normal cursor-pointer transition-colors duration-200 ease-smooth relative overflow-hidden outline-none whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 active:shadow-[inset_0_0_0_9999px_rgba(255,255,255,0.14)]',
  {
    variants: {
      variant: {
        primary: 'bg-accent-blue text-white',
        secondary: 'bg-bg-secondary text-accent-blue',
        tertiary: 'bg-bg-secondary text-label-primary',
        destructiveSecondary: 'bg-bg-secondary text-accent-red',
        white: 'bg-white text-black font-semibold active:shadow-[inset_0_0_0_9999px_rgba(0,0,0,0.10)]',
        success: 'bg-accent-green text-white',
        successSecondary: 'bg-bg-secondary text-accent-green',
        share: 'bg-gradient-to-r from-[#6C5CE7] to-[#a855f7] text-white',
        hero:
          'bg-gradient-to-r from-hero-from to-hero-to text-black shadow-[0_0_20px_rgba(255,215,0,0.25)] active:shadow-[inset_0_0_0_9999px_rgba(0,0,0,0.10),0_0_20px_rgba(255,215,0,0.25)]',
        cta: 'bg-accent-blue text-white relative overflow-hidden before:absolute before:inset-0 before:bg-[linear-gradient(110deg,transparent_25%,rgba(255,255,255,0.3)_50%,transparent_75%)] before:bg-[length:200%_100%] before:animate-[ctaShimmer_4s_ease-in-out_infinite]',
        telegram: 'bg-[#2AABEE] text-white',
        steam: 'bg-[#171a21] text-white',
      },
      size: {
        s: 'px-[10px] py-[4px] text-[15px] leading-[20px] tracking-[-0.23px]',
        m: 'px-[14px] py-[7px] text-[15px] leading-[20px] tracking-[-0.23px]',
        l: 'px-[20px] py-[14px] text-[17px] leading-[22px] tracking-[-0.43px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'm',
    },
  },
);

export const Button: FC<ButtonProps> = ({
  children,
  variant = 'primary',
  mode,
  size = 'm',
  stretched = false,
  loading = false,
  disabled,
  before,
  after,
  iconPosition = 'left',
  className,
  as = 'button',
  ...props
}) => {
  const variantClass = mode ? mode : variant;
  const isIconTop = iconPosition === 'top';
  const motionEnabled = useUiMotionEnabled();

  const content = (
    <>

      <span className={cn('contents', loading && 'invisible')}>
        {before && <span className={cn('flex items-center', !isIconTop && 'me-2')}>{before}</span>}
        {children}
        {after && <span className={cn('flex items-center', !isIconTop && 'ms-2')}>{after}</span>}
      </span>
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size="s" color="white" />
        </span>
      )}
    </>
  );

  const sharedClassName = cn(
    buttonVariants({ variant: variantClass, size }),
    motionEnabled && 'shot-press-tint',
    stretched && 'w-full',
    isIconTop && 'flex-col gap-1',
    className,
  );

  if (as === 'span') {
    return (
      <span className={sharedClassName} {...props}>
        {content}
      </span>
    );
  }

  return (
    <button className={sharedClassName} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>
      {content}
    </button>
  );
};
